/**
 * useNativeShellInit — runs once on app start inside Capacitor (Android / iOS).
 *
 * Fixes the "header buttons merging with status bar clock/icons" bug on Android
 * by telling the StatusBar plugin to stop overlaying the WebView. With overlay
 * turned off, the WebView content starts BELOW the status bar natively — no
 * header needs to worry about safe-area padding on Android.
 *
 * Also installs an `App.addListener('appStateChange', ...)` handler so the
 * WebView repaints on every foreground transition. Without this, Android can
 * leave a stale white surface after the phone is unlocked while the app was
 * already open — the user has to tap the screen to trigger a repaint. The
 * symmetric COLD-START repaint trigger lives in `src/index.js` inside
 * `dismissSplash()` — `appStateChange` does not fire on a fresh launch, so
 * the splash-dismiss path needs its own one-frame transform toggle.
 *
 * On the web / PWA this is a no-op.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { installOtaUpdatePolicy } from '../utils/otaUpdatePolicy';

// Hybrid-clamp ceiling for OS-level text zoom (Android fontScale / iOS Dynamic Type
// surrogate). Honour user preference up to 1.5× (covers "Large/Larger" — the range
// older customers typically pick). Raised from 1.3× once the 360×1.5 responsive
// hardening landed (bottom nav, Cart/Checkout/Profile price columns now reflow-safe
// — see docs/NATIVE_POLISH_PLAN.md Phase 2). Beyond 1.5× we pin; pinch-zoom stays
// enabled on the viewport meta as the WCAG 1.4.4 escape hatch for users who need
// 200%. Industry-standard posture for transactional apps with fixed chrome
// (Zomato / Swiggy / Starbucks-style).
const MAX_TEXT_ZOOM = 1.5;

async function clampTextZoom() {
  try {
    const { TextZoom } = await import('@capacitor/text-zoom');
    const { value: preferred } = await TextZoom.getPreferred();
    const target = Math.min(preferred || 1, MAX_TEXT_ZOOM);
    await TextZoom.set({ value: target });
  } catch { /* plugin missing in PWA build or pre-sync native shell — silent */ }
}

export default function useNativeShellInit() {
  useEffect(() => {
    if (!Capacitor?.isNativePlatform?.()) return;

    // Native visual gate (see docs/NATIVE_POLISH_PLAN.md §3.1): add body classes
    // so the Tailwind `native:` variant and the `body.native` CSS layer in
    // index.css activate. The web PWA never runs this hook (early-return above),
    // so the web stays completely unstyled by it. Set synchronously so native-only
    // styling lands on the first paint.
    document.body.classList.add('native', `platform-${Capacitor.getPlatform()}`);

    let appStateListener = null;
    let otaPolicyListener = null;

    (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        const platform = Capacitor.getPlatform();

        if (platform === 'ios') {
          // iOS: overlay mode — WebView renders edge-to-edge behind the status bar,
          // same as Instagram / WhatsApp / every major iOS app. The status bar
          // becomes transparent; headers already use calc(env(safe-area-inset-top)
          // + Xpx) so they naturally extend to cover the status bar area.
          // Go STRAIGHT to overlay:true. The previous code set overlay:false first
          // (unconditionally, for all platforms) and then re-set true here — that
          // false→true flip resized the WebView viewport for one frame during boot,
          // which dipped the flex-centered splash logo ~166px (captured on-device).
          // No setBackgroundColor call — native status bar is transparent in overlay.
          await StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
          // Style.Dark = "light text for dark backgrounds" (white clock/battery/
          // signal icons) — correct for our dark (bg-primary) headers. (Capacitor's
          // enum is named for the *background*, not the icon colour.)
          await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
        } else {
          // Android: no overlay, white status bar with dark icons.
          await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
          await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
          await StatusBar.setBackgroundColor({ color: '#FFFFFF' }).catch(() => {});
        }
      } catch (e) {
        console.warn('[native-shell] StatusBar init failed', e?.message);
      }

      // The native Capacitor splash is intentionally NOT hidden here any more.
      // It is held over the entire cold-start and lifted only once the app is
      // ready, by `dismissSplash()` in src/index.js (on the `app-ready` event).
      // Hiding it early exposed the HTML #splash underneath, whose flex-centered
      // logo then dipped ~166px the instant the StatusBar calls above resized the
      // WebView viewport. Keeping the stable native splash up hides that settling.

      // Clamp OS text zoom — see MAX_TEXT_ZOOM above for the rationale.
      await clampTextZoom();

      // Ask for location permission upfront so the system prompt is the very
      // first thing the user sees after the app opens. If we waited for
      // Landing.js to call this, it would only fire after branches finish
      // loading — which can feel like the app "isn't asking".
      // Notification permission is handled by usePushNotifications (OneSignal)
      // which listens for 'pb-location-resolved' before showing its dialog —
      // Android only shows one system dialog at a time.
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        const current = await Geolocation.checkPermissions();
        console.info('[native-shell] location checkPermissions →', current);
        const granted = current.location === 'granted' || current.coarseLocation === 'granted';
        const permanentlyDenied = current.location === 'denied' && current.coarseLocation === 'denied';
        if (!granted && !permanentlyDenied) {
          const req = await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
          console.info('[native-shell] location requestPermissions →', req);
          if (req.location === 'granted' || req.coarseLocation === 'granted') {
            sessionStorage.removeItem('branchAutoDetected');
            window.dispatchEvent(new CustomEvent('pb-location-granted'));
          }
        } else if (permanentlyDenied) {
          console.warn('[native-shell] location permanently denied — user must grant via Settings');
        }
      } catch (e) {
        console.warn('[native-shell] location permission request failed:', e?.message, e);
      } finally {
        sessionStorage.setItem('pb-location-resolved', '1');
        window.dispatchEvent(new CustomEvent('pb-location-resolved'));
      }

      // Capgo OTA updater — fetch any pending JS/CSS bundle for this app and
      // notify the native side the web layer is ready.
      //
      // WHEN a downloaded bundle is applied is decided by `utils/otaUpdatePolicy`:
      // on the NEXT COLD START only. Capgo's own default installs the bundle and
      // reloads the WebView the moment the app goes to the background — so a
      // customer switching to WhatsApp for their OTP (or to a UPI app to pay) came
      // back to a restarted app on Home whenever a release had shipped since
      // their last cold start (2026-09-13). The policy is (re)asserted from the
      // plugin's `appReady` event — NOT up front — because the launch-time check
      // is what installs a bundle left waiting by the previous session, and a
      // delay condition present during that check would postpone it by another
      // launch. See the module docstring for the full mechanism.
      try {
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
        // Tell Capgo the current bundle booted successfully (otherwise Capgo
        // will roll back to the previous bundle on next launch).
        await CapacitorUpdater.notifyAppReady();

        otaPolicyListener = await installOtaUpdatePolicy(CapacitorUpdater);

        // Surface lifecycle events in the device console so QA can verify
        // OTA delivery via `adb logcat` or remote-inspect the WebView.
        await CapacitorUpdater.addListener?.('updateAvailable', (info) => {
          console.info('[capgo] update available, version:', info?.bundle?.version, '— applies on next cold start');
        });
        await CapacitorUpdater.addListener?.('downloadComplete', (info) => {
          console.info('[capgo] download complete, version:', info?.bundle?.version);
        });
        await CapacitorUpdater.addListener?.('appReloaded', () => {
          console.info('[capgo] new bundle applied');
        });

        // Debug helper for QA — open Chrome remote-inspector on the device
        // and run `__pbCheckForUpdate()` in the console to force-trigger a
        // sync immediately rather than waiting for the periodic check.
        window.__pbCheckForUpdate = async () => {
          try {
            const res = await CapacitorUpdater.getLatest();
            console.info('[capgo] latest available:', res);
            const sync = await CapacitorUpdater.sync();
            console.info('[capgo] sync result:', sync);
            return { latest: res, sync };
          } catch (err) {
            console.error('[capgo] manual check failed', err);
            return { error: err?.message };
          }
        };
      } catch (e) {
        console.warn('[native-shell] Capgo updater init failed', e?.message);
      }

      // Repaint-on-resume guard. Android's WebView pauses its render pipeline
      // when the app is backgrounded (screen off, app switcher, etc.). On
      // resume it sometimes keeps a stale white surface until something
      // invalidates the view — historically the user had to tap the screen
      // to force a redraw. We catch the foreground transition and:
      //   1. Re-hide the native splash defensively (no-op if already gone).
      //   2. Toggle a transform on <body> to force the WebView to repaint.
      // The COLD-START counterpart of step 2 lives in `src/index.js` inside
      // `dismissSplash()` — `appStateChange` does not fire on a fresh launch,
      // so the splash-dismiss path runs the same transform toggle there.
      try {
        const { App } = await import('@capacitor/app');
        const { SplashScreen } = await import('@capacitor/splash-screen');
        appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) return;
          SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {});
          // Re-clamp text zoom in case the user changed Android Display / Font size
          // while the app was backgrounded — without this the WebView would resume
          // with the new (possibly oversized) system multiplier still applied.
          clampTextZoom();
          const body = document.body;
          if (!body) return;
          body.style.transform = 'translateZ(0)';
          requestAnimationFrame(() => {
            body.style.transform = '';
          });
        });
      } catch (e) {
        console.warn('[native-shell] appStateChange listener failed', e?.message);
      }
    })();

    return () => {
      appStateListener?.remove?.();
      otaPolicyListener?.remove?.();
    };
  }, []);
}
